import { useEffect, useRef, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";

import { cn } from "~/lib/utils";

/**
 * One-click copy.
 *
 * The realistic workflow on every screen here is lifting an identifier into
 * DurianPay's dashboard or a Slack thread. A page that makes someone select
 * text out of a table cell to do that is a page that gets used through a
 * database client instead.
 *
 * Feedback is a check mark for two seconds rather than a toast: this fires many
 * times in a row while someone collects ids, and a stack of toasts for a
 * succeeded copy is noise.
 */
export function CopyButton({
  value,
  label,
  className,
}: {
  value: string;
  /** What was copied, for screen readers. Defaults to the value itself. */
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Clearing on unmount keeps setState off a component that has gone away when
  // someone copies an id and immediately navigates.
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Denied permission, or an insecure origin. Say nothing rather than
      // claim a copy that did not happen.
      return;
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label={copied ? "Copied" : `Copy ${label ?? value}`}
      title={copied ? "Copied" : "Copy"}
      className={cn(
        "inline-grid size-6 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none",
        className,
      )}
    >
      {copied ? (
        <CheckIcon className="size-3.5 text-success" />
      ) : (
        <CopyIcon className="size-3.5" />
      )}
    </button>
  );
}

/**
 * An identifier and its copy button. The pairing every id on every screen uses,
 * so the affordance is in the same place each time.
 */
export function CopyableId({
  value,
  label,
  className,
  mono = true,
}: {
  value: string;
  label?: string;
  className?: string;
  /** Off for values that are prose rather than an identifier, like a URL. */
  mono?: boolean;
}) {
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1", className)}>
      <span className={cn("truncate", mono && "font-mono text-[0.8125rem]")}>{value}</span>
      <CopyButton value={value} label={label} />
    </span>
  );
}
